import React from "react";

const same = (a, b) =>
  String(a ?? "")
    .trim()
    .toLowerCase() ===
  String(b ?? "")
    .trim()
    .toLowerCase();

// Text colours of the StatusIcons themes, so a number can match its
// status chip.
const TONES = {
  success: "text-state-success-text",
  warning: "text-state-warning-text",
  danger: "text-state-danger-text",
  info: "text-state-info-text",
  neutral: "text-state-neutral-text",
  purple: "text-state-purple-text",
  sky: "text-state-sky-text",
};

// Every visible column's headerStats, in column order (for the phone chips).
export const collectHeaderStats = (columns) =>
  (columns ?? [])
    .filter((col) => col?.checked !== false && !col?.collapsed)
    .flatMap((col) => col?.headerStats ?? []);

export const isStatOn = (stat, filterValues) =>
  Boolean(stat?.filter) &&
  same(filterValues?.[stat.filter.field_name], stat.filter.value);

/**
 * Numbers of a column heading (`column.headerStats`: { key, label, tone?,
 * title?, filter?: { field_name, value } }). A stat with a filter is a
 * button: clicking it applies that filter (the same &field=value as the
 * heading pop-up), clicking it again removes it. Under the heading on
 * desktop; as a row of chips on phones.
 */
export default function HeaderStats({
  stats,
  filterValues,
  onApply,
  variant = "heading",
}) {
  if (!stats?.length) return null;

  const toggle = (event, stat) => {
    event.stopPropagation();
    onApply(
      stat.filter.field_name,
      isStatOn(stat, filterValues) ? "" : stat.filter.value,
    );
  };

  if (variant === "chips") {
    return stats.map((stat) => {
      const on = isStatOn(stat, filterValues);
      const chipClass = `inline-flex shrink-0 items-center whitespace-nowrap rounded-full border-[1px] px-[10px] py-[5px] text-xs font-medium ${
        on
          ? "border-brand bg-brand-light text-brand"
          : `border-ui-border bg-ui-surface ${TONES[stat.tone] ?? "text-ui-text-muted"}`
      }`;
      return stat.filter ? (
        <button
          key={stat.key}
          type="button"
          aria-pressed={on}
          title={stat.title}
          onClick={(event) => toggle(event, stat)}
          className={chipClass}
        >
          {stat.label}
        </button>
      ) : (
        <span key={stat.key} title={stat.title} className={chipClass}>
          {stat.label}
        </span>
      );
    });
  }

  return (
    <span className="mt-[2px] flex min-w-0 items-center gap-[6px] overflow-hidden text-[9.5px] font-normal normal-case leading-[14px] tracking-normal">
      {stats.map((stat) => {
        const on = isStatOn(stat, filterValues);
        const tone = TONES[stat.tone] ?? "text-ui-text-subtle";
        return stat.filter ? (
          <button
            key={stat.key}
            type="button"
            aria-pressed={on}
            title={stat.title}
            onClick={(event) => toggle(event, stat)}
            className={`shrink-0 rounded border-0 px-[3px] font-medium transition-colors hover:underline ${
              on ? "bg-brand-light text-brand" : `bg-transparent ${tone}`
            }`}
          >
            {stat.label}
          </button>
        ) : (
          <span
            key={stat.key}
            title={stat.title}
            className="shrink-0 text-ui-text-subtle"
          >
            {stat.label}
          </span>
        );
      })}
    </span>
  );
}
